import {
  types,
  isLateType,
  getParentOfType,
  isUnionType,
  type IAnyComplexType,
  type IAnyStateTreeNode,
  type IAnyType,
  type IReferenceType,
  type ReferenceIdentifier,
  type ReferenceOptionsGetSet,
} from 'mobx-state-tree';
import Model from './index';

// `getSubType`/`getSubTypes` are runtime accessors on MST's concrete Late/Union
// type instances. They are NOT part of the public `IAnyType`/`IUnionType` type
// surface, so we declare the minimal shapes we actually call. This is the only
// MST limitation we route around here — everything else is fully typed.
interface LateTypeWithSubType extends IAnyType {
  getSubType(): IAnyType;
}
interface UnionTypeWithSubTypes extends IAnyType {
  getSubTypes(): IAnyType[];
}

// `./index` (the root Model store) is still untyped .js, so its instance and the
// `.metamodel` lookups arrive as `any` at this boundary. That is acceptable per
// the incremental migration: the metamodel resolver methods are exercised by the
// golden-snapshot tests, and typing the root Model is a later layer's job.
type ResolvableModel = {
  metamodel: {
    getEventTypeById(id: ReferenceIdentifier): IAnyStateTreeNode | undefined;
    getObjectTypeById(id: ReferenceIdentifier): IAnyStateTreeNode | undefined;
    getDependencyById(id: ReferenceIdentifier): IAnyStateTreeNode | undefined;
    getMethodById(id: ReferenceIdentifier): IAnyStateTreeNode | undefined;
    getDataTypeById(id: ReferenceIdentifier): IAnyStateTreeNode | undefined;
    getFSMById(id: ReferenceIdentifier): IAnyStateTreeNode | undefined;
    getStateById(id: ReferenceIdentifier): IAnyStateTreeNode | undefined;
    getTransitionById(id: ReferenceIdentifier): IAnyStateTreeNode | undefined;
    getInheritanceById(id: ReferenceIdentifier): IAnyStateTreeNode | undefined;
  };
};

/**
 * Helper function to resolve a model reference given a model, the type and the identifier.
 *
 * @param model Merlin model
 * @param type The referenced type. Must be one of the model types, late or union types must
 * have been resolved first.
 * @param identifier The id of the reference element
 */
function resolveModelIdentifier(
  model: ResolvableModel,
  type: IAnyType,
  identifier: ReferenceIdentifier
): IAnyStateTreeNode | undefined {
  switch (type.name) {
    case 'EventType':
      return model.metamodel.getEventTypeById(identifier);
    case 'ObjectType':
      return model.metamodel.getObjectTypeById(identifier);
    case 'Dependency':
      return model.metamodel.getDependencyById(identifier);
    case 'Method':
      return model.metamodel.getMethodById(identifier);
    case 'DataType':
      return model.metamodel.getDataTypeById(identifier);
    case 'FSM':
      return model.metamodel.getFSMById(identifier);
    case 'State':
      return model.metamodel.getStateById(identifier);
    case 'Transition':
      return model.metamodel.getTransitionById(identifier);
    case 'Inheritance':
      return model.metamodel.getInheritanceById(identifier);
    default:
      throw new Error(`Unknown type for a model reference: ${type.name}`);
  }
}

// Memoize the owning Model per node. We deliberately do NOT stash this on the
// node itself: MST 7 + mobx 6 make instances protected observables, so assigning
// an ad-hoc `__model` property outside an action now throws. A WeakMap keeps the
// exact same caching behaviour without mutating the observable.
const modelCache = new WeakMap<object, ResolvableModel>();

function getModel(parent: IAnyStateTreeNode): ResolvableModel {
  let model = modelCache.get(parent);
  if (model == null) {
    // `Model` is still untyped .js, so `getParentOfType` yields `any` here; we
    // narrow to the resolver shape we depend on.
    model = getParentOfType(parent, Model) as ResolvableModel;
    modelCache.set(parent, model);
  }
  return model;
}

/**
 * Custom reference type that resolves identifiers within the scope of the
 * model.
 *
 * MobX State Tree stores references as ids in the serialization, but it
 * transparently resolves those when you access the reference property.
 *
 * The builtin resolveIdentifier of MobX State Tree searches for a node with the
 * correct type and identifier in the complete MST tree. Since we store multiple
 * versions of the same model, this results in duplicate ids for the same type
 * and the resolver cannot unambiguously resolve identifiers anymore.
 * Consequence: it throws an error.
 *
 * Therefore we use a customized resolver that only searches within the current
 * model.
 *
 * @param type the target type for the reference
 *
 * TYPING the recursive references (the crux of the metamodel migration):
 *  - The runtime `type` is frequently a `types.late((): IAnyModelType => X)` so
 *    the value-level circular dependency between the mutually recursive models is
 *    broken (see the model files). With `IAnyModelType` the late callback no
 *    longer feeds its own inference, which is what stops TS' "referenced directly
 *    or indirectly in its own initializer" error.
 *  - But `IAnyModelType` resolves to a *loose* instance, which would make every
 *    `self.master.…` read untyped. To get the concrete instance type back, the
 *    phantom type parameter `IT` controls ONLY the return type. Callers pass the
 *    concrete model type explicitly, e.g.
 *      `modelReference<typeof ObjectType>(types.late((): IAnyModelType => ObjectType))`
 *    so the reference property reads as `Instance<typeof ObjectType>` while the
 *    runtime argument stays the cycle-breaking late type.
 *
 * NOTE on nullability: the resolver returns `undefined` for a dangling id (an
 * orphaned reference). MST's `ReferenceOptionsGetSet.get` is typed to return a
 * non-undefined instance, so we cast the resolver result to MST's expected
 * return type at the boundary. At runtime, accessing such a reference property
 * therefore yields `T | undefined` — callers that touch potentially-dangling
 * refs already guard with `types.maybe` / `types.maybeNull` or null checks.
 */
export function modelReference<IT extends IAnyComplexType = IAnyComplexType>(
  type: IAnyComplexType
): IReferenceType<IT> {
  type RefValue = IAnyStateTreeNode & { id: ReferenceIdentifier };
  // The get/set bodies are fully typed; we cast the assembled reference to the
  // caller-chosen `IT` so the model property reads as the concrete instance.
  const ref = types.reference<IAnyComplexType>(type, {
    get(identifier: ReferenceIdentifier, parent: IAnyStateTreeNode | null): RefValue {
      const model = getModel(parent as IAnyStateTreeNode);
      if (isUnionType(type)) {
        const subTypes = (type as unknown as UnionTypeWithSubTypes).getSubTypes();
        for (const type of subTypes) {
          const resolved = resolveModelIdentifier(model, type, identifier);
          if (resolved != null) {
            return resolved as RefValue;
          }
        }
        return undefined as unknown as RefValue;
      } else if (isLateType(type)) {
        return resolveModelIdentifier(
          model,
          (type as unknown as LateTypeWithSubType).getSubType(),
          identifier
        ) as RefValue;
      } else {
        return resolveModelIdentifier(model, type, identifier) as RefValue;
      }
    },
    set(value: RefValue) {
      return value.id;
    },
  } as ReferenceOptionsGetSet<IAnyComplexType>);
  return ref as unknown as IReferenceType<IT>;
}
